// App shell — routes are CODE-SPLIT (React.lazy) so the initial download is
// small enough to load fast on cellular. Only Landing + Login ship eagerly;
// every other page loads on demand as its own chunk.
import { Suspense } from "react";
import { lazyRetry as lazy } from "@/lib/lazyRetry";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import OwnerPreviewBanner from "@/components/OwnerPreviewBanner";
import "./App.css";

// lazy() for a NAMED export inside a grouped module (chunk downloads once)
const lazyNamed = (loader, name) => lazy(() => loader().then((m) => ({ default: m[name] })));

const DashboardLayout = lazy(() => import("@/components/DashboardLayout"));
const Register = lazy(() => import("@/pages/Register"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("@/pages/TermsOfService"));
const BillingSuccess = lazy(() => import("@/pages/BillingSuccess"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const RepDashboard = lazy(() => import("@/pages/RepDashboard"));
const CommissionLedgerPage = lazy(() => import("@/pages/CommissionLedgerPage"));
const RepQuotingPage = lazy(() => import("@/pages/RepQuotingPage"));
const PartnerOnboardingPage = lazy(() => import("@/pages/PartnerOnboardingPage"));
const PartnerNetworkReviewPage = lazy(() => import("@/pages/PartnerNetworkReviewPage"));
const RepOffersPage = lazy(() => import("@/pages/RepOffersPage"));
const MarketplacePage = lazy(() => import("@/pages/MarketplacePage"));
const RepLogin = lazy(() => import("@/pages/RepLogin"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const RepMarketplacePage = lazy(() => import("@/pages/RepMarketplacePage"));
const OwnerLogin = lazy(() => import("@/pages/OwnerLogin"));
const OwnerPortal = lazy(() => import("@/pages/OwnerPortal"));
const AuthCallback = lazy(() => import("@/pages/AuthCallback"));

const coreSheets = () => import("@/pages/sheets/CoreSheets");
const FixedCostsPage = lazyNamed(coreSheets, "FixedCostsPage");
const ServicesPage = lazyNamed(coreSheets, "ServicesPage");
const PriceGuidePage = lazyNamed(coreSheets, "PriceGuidePage");
const JobsPage = lazyNamed(coreSheets, "JobsPage");
const QuickQuotePage = lazyNamed(coreSheets, "QuickQuotePage");
const WeeklySchedulePage = lazyNamed(coreSheets, "WeeklySchedulePage");
const BookingsPage = lazyNamed(coreSheets, "BookingsPage");
const ClientsPage = lazyNamed(coreSheets, "ClientsPage");
const FinancingLoansPage = lazyNamed(coreSheets, "FinancingLoansPage");
const LoanPaymentsPage = lazyNamed(coreSheets, "LoanPaymentsPage");
const PaymentSchedulePage = lazyNamed(coreSheets, "PaymentSchedulePage");
const DefaultTrackerPage = lazyNamed(coreSheets, "DefaultTrackerPage");

const RewardsLoyaltyPage = lazy(() => import("@/pages/sheets/RewardsLoyaltyPage"));
const BusinessAccountingHub = lazy(() => import("@/pages/accounting/BusinessAccountingHub"));
const CompanyDebtPage = lazy(() => import("@/pages/accounting/CompanyDebtPage"));
const ChartOfAccountsPage = lazy(() => import("@/pages/accounting/ChartOfAccountsPage"));
const PLStatementPage = lazy(() => import("@/pages/accounting/PLStatementPage"));
const CashFlowStatementPage = lazy(() => import("@/pages/accounting/CashFlowStatementPage"));
const BalanceSheetPage = lazy(() => import("@/pages/accounting/BalanceSheetPage"));
const OwnerTransfersPage = lazy(() => import("@/pages/accounting/OwnerTransfersPage"));
const LedgerCategorizerPage = lazy(() => import("@/pages/accounting/LedgerCategorizerPage"));
const MissingReceiptsPage = lazy(() => import("@/pages/accounting/MissingReceiptsPage"));
const ChainTracePage = lazy(() => import("@/pages/accounting/ChainTracePage"));
const MonthlyCloseoutPage = lazy(() => import("@/pages/accounting/MonthlyCloseoutPage"));
const MonthlyArchivePage = lazy(() => import("@/pages/MonthlyArchivePage"));
const DispatchBoardPage = lazy(() => import("@/pages/DispatchBoardPage"));
const RepDropoffPage = lazy(() => import("@/pages/RepDropoffPage"));
const QrRedeemPage = lazy(() => import("@/pages/QrRedeemPage"));
const PublicEnquiryPage = lazy(() => import("@/pages/PublicEnquiryPage"));
const QrSettingsPage = lazy(() => import("@/pages/QrSettingsPage"));
const LegalConsentsPage = lazy(() => import("@/pages/LegalConsentsPage"));
const RainGuardPage = lazy(() => import("@/pages/RainGuardPage"));
const WeatherReschedulePage = lazy(() => import("@/pages/WeatherReschedulePage"));
const LeadDashboardPage = lazy(() => import("@/pages/leads/LeadDashboardPage"));
const MyLeadsPage = lazy(() => import("@/pages/leads/MyLeadsPage"));
const LeadPipelinePage = lazy(() => import("@/pages/leads/LeadPipelinePage"));
const LeadAutomationPage = lazy(() => import("@/pages/leads/LeadAutomationPage"));
const CallQueuePage = lazy(() => import("@/pages/leads/CallQueuePage"));
const RetentionPage = lazy(() => import("@/pages/leads/RetentionPage"));
const AutoRulesPage = lazy(() => import("@/pages/accounting/AutoRulesPage"));
const AccountantAccessPage = lazy(() => import("@/pages/accounting/AccountantAccessPage"));
const AuditLogPage = lazy(() => import("@/pages/accounting/AuditLogPage"));
const TaxPackagePage = lazy(() => import("@/pages/accounting/TaxPackagePage"));
const AccountantLogin = lazy(() => import("@/pages/accountant/AccountantLogin"));
const AccountantAccessLink = lazy(() => import("@/pages/accountant/AccountantAccessLink"));
const AccountantPortal = lazy(() => import("@/pages/accountant/AccountantPortal"));
const AILeadsPage = lazy(() => import("@/pages/ai_intel/AILeadsPage"));
const AILeadsROIPage = lazy(() => import("@/pages/ai_intel/AILeadsROIPage"));
const AdminAIIntelPage = lazy(() => import("@/pages/ai_intel/AdminAIIntelPage"));

const opsSheets = () => import("@/pages/sheets/OpsSheets");
const MonthlyDashboardPage = lazyNamed(opsSheets, "MonthlyDashboardPage");
const AccountingDashboardPage = lazyNamed(opsSheets, "AccountingDashboardPage");
const BillsPage = lazyNamed(opsSheets, "BillsPage");
const TransactionsPage = lazyNamed(opsSheets, "TransactionsPage");
const TaxSavingsPage = lazyNamed(opsSheets, "TaxSavingsPage");
const BusinessLoansPage = lazyNamed(opsSheets, "BusinessLoansPage");
const BusinessLoanPaymentsPage = lazyNamed(opsSheets, "BusinessLoanPaymentsPage");
const CompanySavingsPage = lazyNamed(opsSheets, "CompanySavingsPage");
const ChemicalPriceHistoryPage = lazyNamed(opsSheets, "ChemicalPriceHistoryPage");
const GasTravelPage = lazyNamed(opsSheets, "GasTravelPage");
const MileageLogPage = lazyNamed(opsSheets, "MileageLogPage");
const ResinPage = lazyNamed(opsSheets, "ResinPage");
const PayrollPage = lazyNamed(opsSheets, "PayrollPage");
const WorkAssignmentsPage = lazyNamed(opsSheets, "WorkAssignmentsPage");
const CCRewardsPage = lazyNamed(opsSheets, "CCRewardsPage");
const SettingsPage = lazyNamed(opsSheets, "SettingsPage");
const InstructionsPage = lazyNamed(opsSheets, "InstructionsPage");

const ChemicalInventoryPage = lazy(() => import("@/pages/sheets/ChemicalInventoryPage"));
const ChemicalIntelligencePage = lazy(() => import("@/pages/ChemicalIntelligencePage"));
const ChemicalDetailPage = lazy(() => import("@/pages/ChemicalDetailPage"));
const ChemIntelSettingsPage = lazy(() => import("@/pages/ChemIntelSettingsPage"));
const ChemAlertsPage = lazy(() => import("@/pages/ChemAlertsPage"));
const ChemPurchaseScannerPage = lazy(() => import("@/pages/ChemPurchaseScannerPage"));
const ChemForecastPage = lazy(() => import("@/pages/ChemForecastPage"));
const ChemQuoteAdjustPage = lazy(() => import("@/pages/ChemQuoteAdjustPage"));
const ChemScrapeRunsPage = lazy(() => import("@/pages/ChemScrapeRunsPage"));
const ChemJobCloseoutPage = lazy(() => import("@/pages/ChemJobCloseoutPage"));
const MasterCatalogPage = lazy(() => import("@/pages/MasterCatalogPage"));
const ArchitecturePage = lazy(() => import("@/pages/dev/ArchitecturePage"));
const ChemAddFromCatalogPage = lazy(() => import("@/pages/ChemAddFromCatalogPage"));
const DiResinManagerPage = lazy(() => import("@/pages/DiResinManagerPage"));
const FieldTabPage = lazy(() => import("@/pages/FieldTabPage"));
const ServiceKitsPage = lazy(() => import("@/pages/sheets/ServiceKitsPage"));
const CrossCityPlannerPage = lazy(() => import("@/pages/sheets/CrossCityPlannerPage"));
const LocalRoutesPage = lazy(() => import("@/pages/sheets/LocalRoutesPage"));
const EquipmentPage = lazy(() => import("@/pages/sheets/EquipmentPage"));
const FinancingPlansPage = lazy(() => import("@/pages/sheets/FinancingPlansPage"));
const FinancingHubPage = lazy(() => import("@/pages/sheets/FinancingHubPage"));
const JobRiskCalculatorPage = lazy(() => import("@/pages/sheets/JobRiskCalculatorPage"));
const VehiclesPage = lazy(() => import("@/pages/vehicles/VehiclesPage"));
const VehicleDetailPage = lazy(() => import("@/pages/vehicles/VehicleDetailPage"));
const InvoicesPage = lazy(() => import("@/pages/sheets/InvoicesPage"));
const InvoiceDetailPage = lazy(() => import("@/pages/sheets/InvoiceDetailPage"));
const PublicInvoicePage = lazy(() => import("@/pages/PublicInvoicePage"));
const IntakePublicPage = lazy(() => import("@/pages/IntakePublicPage"));
const QuotePublicPage = lazy(() => import("@/pages/QuotePublicPage"));
const ClientIntakesPage = lazy(() => import("@/pages/sheets/ClientIntakesPage"));
const QuotesPage = lazy(() => import("@/pages/sheets/QuotesPage"));
const ClientDetailPage = lazy(() => import("@/pages/clients/ClientDetailPage"));
const WorkerSignupPage = lazy(() => import("@/pages/WorkerSignupPage"));
const TeamPage = lazy(() => import("@/pages/team/TeamPage"));
const TeamAvailabilityPage = lazy(() => import("@/pages/team/TeamAvailabilityPage"));
const MyAvailabilityPage = lazy(() => import("@/pages/availability/MyAvailabilityPage"));
const MyProfilePage = lazy(() => import("@/pages/MyProfilePage"));
const PerformanceAdminPage = lazy(() => import("@/pages/PerformanceAdminPage"));
const PerfSettingsPage = lazy(() => import("@/pages/PerfSettingsPage"));
const MyPerformancePage = lazy(() => import("@/pages/MyPerformancePage"));
const PublicReviewPage = lazy(() => import("@/pages/PublicReviewPage"));
const I9CompliancePage = lazy(() => import("@/pages/I9CompliancePage"));
const HydrationStationPage = lazy(() => import("@/pages/HydrationStationPage"));
const HeatSafetyPage = lazy(() => import("@/pages/HeatSafetyPage"));
const RedemptionQueuePage = lazy(() => import("@/pages/loyalty/RedemptionQueuePage"));
const CustomerLoginPage = lazy(() => import("@/pages/customer/CustomerLoginPage"));
const CustomerSetupPage = lazy(() => import("@/pages/customer/CustomerSetupPage"));
const CustomerDashboardPage = lazy(() => import("@/pages/customer/CustomerDashboardPage"));
const BonusTrackerPage = lazy(() => import("@/pages/sheets/BonusTrackerPage"));
const SoftwareReferralsPage = lazy(() => import("@/pages/sheets/SoftwareReferralsPage"));

const salesRepPages = () => import("@/pages/sheets/SalesRepPages");
const SalesRepsPage = lazyNamed(salesRepPages, "SalesRepsPage");
const QRCodesPage = lazyNamed(salesRepPages, "QRCodesPage");
const CardPlacementsPage = lazyNamed(salesRepPages, "CardPlacementsPage");
const RepBookingsPage = lazyNamed(salesRepPages, "RepBookingsPage");
const DoorToDoorPage = lazyNamed(salesRepPages, "DoorToDoorPage");
const SocialReferralsPage = lazyNamed(salesRepPages, "SocialReferralsPage");
const CommissionSettingsPage = lazyNamed(salesRepPages, "CommissionSettingsPage");
const CommissionLookupPage = lazyNamed(salesRepPages, "CommissionLookupPage");
const SalesCompanyDashboard = lazyNamed(salesRepPages, "SalesCompanyDashboard");

const toolRentalPages = () => import("@/pages/sheets/ToolRentalPages");
const ToolCatalogPage = lazyNamed(toolRentalPages, "ToolCatalogPage");
const PackageBuilderPage = lazyNamed(toolRentalPages, "PackageBuilderPage");
const CustomerRegistryPage = lazyNamed(toolRentalPages, "CustomerRegistryPage");
const RentalOrdersPage = lazyNamed(toolRentalPages, "RentalOrdersPage");
const ReturnsDamagesPage = lazyNamed(toolRentalPages, "ReturnsDamagesPage");
const RepairLogPage = lazyNamed(toolRentalPages, "RepairLogPage");
const PaymentsDepositsPage = lazyNamed(toolRentalPages, "PaymentsDepositsPage");
const RentToOwnPage = lazyNamed(toolRentalPages, "RentToOwnPage");
const RentalFixedCostsPage = lazyNamed(toolRentalPages, "RentalFixedCostsPage");
const AvailabilityPage = lazyNamed(toolRentalPages, "AvailabilityPage");
const TrustScorePage = lazyNamed(toolRentalPages, "TrustScorePage");
const AssetLifecyclePage = lazyNamed(toolRentalPages, "AssetLifecyclePage");
const LateFeePolicyPage = lazyNamed(toolRentalPages, "LateFeePolicyPage");
const MarketingRoiPage = lazyNamed(toolRentalPages, "MarketingRoiPage");
const CapitalAllocationPage = lazyNamed(toolRentalPages, "CapitalAllocationPage");
const PnlStatementPage = lazyNamed(toolRentalPages, "PnlStatementPage");
const FinancialDashboardPage = lazyNamed(toolRentalPages, "FinancialDashboardPage");
const RentalSettingsPage = lazyNamed(toolRentalPages, "RentalSettingsPage");
const ToolRentalOverviewPage = lazyNamed(toolRentalPages, "ToolRentalOverviewPage");

// PRO — Personal Finance workspace
const PfDashboardPage = lazy(() => import("@/pages/finance/PfDashboardPage"));
const PfDailyLogPage = lazy(() => import("@/pages/finance/PfDailyLogPage"));
const PfMonthlyBudgetPage = lazy(() => import("@/pages/finance/PfMonthlyBudgetPage"));
const PfBillsPage = lazy(() => import("@/pages/finance/PfBillsPage"));
const PfNetWorthPage = lazy(() => import("@/pages/finance/PfNetWorthPage"));
const PfDebtsPage = lazy(() => import("@/pages/finance/PfDebtsPage"));
const PfPayoffSchedulePage = lazy(() => import("@/pages/finance/PfPayoffSchedulePage"));
const PfGoalsPage = lazy(() => import("@/pages/finance/PfGoalsPage"));
const PfSettingsPage = lazy(() => import("@/pages/finance/PfSettingsPage"));
const PfPlaidPage = lazy(() => import("@/pages/finance/PfPlaidPage"));
const CompanyWorthPage = lazy(() => import("@/pages/finance/CompanyWorthPage"));
const PfIncomePage = lazy(() => import("@/pages/finance/PfIncomePage"));
const PfCreditCardsPage = lazy(() => import("@/pages/finance/PfCreditCardsPage"));
const PfReviewPage = lazy(() => import("@/pages/finance/PfReviewPage"));
const PfCashFlowPage = lazy(() => import("@/pages/finance/PfCashFlowPage"));
const PfReportsPage = lazy(() => import("@/pages/finance/PfReportsPage"));
const PfEmergencyFundPage = lazy(() => import("@/pages/finance/PfEmergencyFundPage"));
const PfRecurringPage = lazy(() => import("@/pages/finance/PfRecurringPage"));
const SurvivalModePage = lazy(() => import("@/pages/finance/SurvivalModePage"));
const PfHouseholdPage = lazy(() => import("@/pages/finance/PfHouseholdPage"));
const MaintenanceContractsPage = lazy(() => import("@/pages/MaintenanceContractsPage"));
const MarketHealthPage = lazy(() => import("@/pages/MarketHealthPage"));
const ContractSignPage = lazy(() => import("@/pages/ContractSignPage"));
const InspectionAckPage = lazy(() => import("@/pages/InspectionAckPage"));
const CustomerPortalPage = lazy(() => import("@/pages/CustomerPortalPage"));
const LiveEtaPage = lazy(() => import("@/pages/LiveEtaPage"));
const AcademyMarketplacePage = lazy(() => import("@/pages/academy/AcademyMarketplacePage"));
const AcademyCoursePage = lazy(() => import("@/pages/academy/AcademyCoursePage"));
const AcademyLearnPage = lazy(() => import("@/pages/academy/AcademyLearnPage"));
const AcademyMePage = lazy(() => import("@/pages/academy/AcademyMePage"));
const AcademySignupPage = lazy(() => import("@/pages/academy/AcademySignupPage"));
const AcademyProviderPage = lazy(() => import("@/pages/academy/AcademyProviderPage"));
const AcademyCourseBuilderPage = lazy(() => import("@/pages/academy/AcademyCourseBuilderPage"));
const AcademyCourseStudentsPage = lazy(() => import("@/pages/academy/AcademyCourseStudentsPage"));
const AcademyInvitePage = lazy(() => import("@/pages/academy/AcademyInvitePage"));
const AcademyAdminPage = lazy(() => import("@/pages/academy/AcademyAdminPage"));
const VerifyCertPage = lazy(() => import("@/pages/academy/VerifyCertPage"));

// Route-transition fallback — shown while a page chunk downloads
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--ld-bg,#050508)]" data-testid="page-loader">
      <div className="w-8 h-8 rounded-full border-2 border-slate-600 border-t-cyan-400 animate-spin" />
    </div>
  );
}

// Google OAuth return leg: session_id arrives in the URL fragment. Detect it
// synchronously during render (before any protected-route auth checks run).
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function HashSessionGate({ children }) {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return children;
}

// If user is authenticated, redirect landing/login to /app (reps go to /rep)
function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) {
    const dest = user.role === "rep" ? (user.parent_user_id ? "/rep" : "/academy")
      : user.role === "customer" ? "/customer/dashboard"
      : user.role === "pf_partner" ? "/app/finance/household" : "/app";
    return <Navigate to={dest} replace />;
  }
  return children;
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="bottom-right" theme="system" richColors duration={2500} />
          <OwnerPreviewBanner />
        <Suspense fallback={<PageLoader />}>
        <HashSessionGate>
        <Routes>
          <Route path="/" element={<PublicOnly><Landing /></PublicOnly>} />
          <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
          <Route path="/forgot-password" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
          <Route path="/reset-password/:token" element={<PublicOnly><ResetPassword /></PublicOnly>} />
          <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/billing/success" element={<BillingSuccess />} />
          <Route path="/invoice/:token" element={<PublicInvoicePage />} />
          <Route path="/intake/:token" element={<IntakePublicPage />} />
          <Route path="/quote/:token" element={<QuotePublicPage />} />
          <Route path="/reschedule/:token" element={<WeatherReschedulePage />} />
          <Route path="/sign/:token" element={<ContractSignPage />} />
          <Route path="/inspection/ack/:token" element={<InspectionAckPage />} />
          <Route path="/portal/:token" element={<CustomerPortalPage />} />
          <Route path="/eta/:shareToken" element={<LiveEtaPage />} />
          <Route path="/team-invite/:token" element={<WorkerSignupPage />} />
          <Route path="/review/:token" element={<PublicReviewPage />} />
          <Route path="/qr/:token" element={<QrRedeemPage />} />
          <Route path="/enquiry/:captureKey" element={<PublicEnquiryPage />} />
          <Route path="/customer/login" element={<CustomerLoginPage />} />
          {/* Academy — public marketplace + rep experience */}
          <Route path="/academy" element={<AcademyMarketplacePage />} />
          <Route path="/academy/signup" element={<AcademySignupPage />} />
          <Route path="/academy/course/:id" element={<AcademyCoursePage />} />
          <Route path="/academy/invite/:token" element={<AcademyInvitePage />} />
          <Route path="/academy/learn/:eid" element={<ProtectedRoute><AcademyLearnPage /></ProtectedRoute>} />
          <Route path="/academy/me" element={<ProtectedRoute><AcademyMePage /></ProtectedRoute>} />
          <Route path="/verify/:certId" element={<VerifyCertPage />} />
          <Route path="/customer/setup/:token" element={<CustomerSetupPage />} />
          <Route path="/customer/dashboard" element={<CustomerDashboardPage />} />
          <Route path="/rep/login" element={<RepLogin />} />
          <Route path="/rep" element={<ProtectedRoute><RepDashboard /></ProtectedRoute>} />
          <Route path="/rep/commissions" element={<ProtectedRoute><div className="min-h-screen bg-slate-950 text-white p-4 sm:p-6"><CommissionLedgerPage /></div></ProtectedRoute>} />
          <Route path="/rep/quoting" element={<ProtectedRoute><div className="min-h-screen bg-slate-950 text-white p-4 sm:p-6"><RepQuotingPage /></div></ProtectedRoute>} />
          <Route path="/rep/partner-network" element={<ProtectedRoute><div className="min-h-screen bg-slate-950 text-white p-4 sm:p-6"><PartnerOnboardingPage /></div></ProtectedRoute>} />
          <Route path="/rep/offers" element={<ProtectedRoute><div className="min-h-screen bg-slate-950 text-white p-4 sm:p-6"><RepOffersPage /></div></ProtectedRoute>} />
          <Route path="/rep/sales-marketplace" element={<ProtectedRoute><div className="min-h-screen bg-slate-950 text-white p-4 sm:p-6"><MarketplacePage /></div></ProtectedRoute>} />
          <Route path="/owner/login" element={<OwnerLogin />} />
          <Route path="/owner" element={<OwnerPortal />} />
          {/* Accountant portal — public, uses its own token (accountant_token) */}
          <Route path="/accountant/login" element={<AccountantLogin />} />
          <Route path="/accountant/access" element={<AccountantAccessLink />} />
          <Route path="/accountant/access/:token" element={<AccountantAccessLink />} />
          <Route path="/accountant/portal" element={<AccountantPortal />} />
          <Route path="/app/rep/marketplace" element={<ProtectedRoute><DashboardLayout><RepMarketplacePage /></DashboardLayout></ProtectedRoute>} />
          <Route path="/rep/marketplace" element={<ProtectedRoute><RepMarketplacePage /></ProtectedRoute>} />
          <Route path="/rep/my-leads" element={<ProtectedRoute><div className="min-h-screen bg-[var(--ld-bg)] text-white p-4 md:p-8"><div className="max-w-5xl mx-auto"><MyLeadsPage /></div></div></ProtectedRoute>} />
          <Route path="/rep/ai-leads" element={<ProtectedRoute><div className="min-h-screen bg-[var(--ld-bg)] text-white p-4 md:p-8"><div className="max-w-5xl mx-auto"><AILeadsPage /></div></div></ProtectedRoute>} />
          <Route path="/rep/ai-leads/roi" element={<ProtectedRoute><AILeadsROIPage /></ProtectedRoute>} />
          <Route path="/app/ai-leads/roi" element={<ProtectedRoute><AILeadsROIPage /></ProtectedRoute>} />
          <Route path="/app" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
            <Route index element={<Dashboard />} />
            <Route path="instructions" element={<InstructionsPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="legal-consents" element={<LegalConsentsPage />} />
            <Route path="rain-guard" element={<RainGuardPage />} />
            <Route path="leads" element={<LeadDashboardPage />} />
            <Route path="my-leads" element={<MyLeadsPage />} />
            <Route path="leads/pipeline" element={<LeadPipelinePage />} />
            <Route path="leads/automation" element={<LeadAutomationPage />} />
            <Route path="leads/calls" element={<CallQueuePage />} />
            <Route path="leads/retention" element={<RetentionPage />} />
            <Route path="academy/provider" element={<AcademyProviderPage />} />
            <Route path="academy/provider/course/:id" element={<AcademyCourseBuilderPage />} />
            <Route path="academy/provider/course/:id/students" element={<AcademyCourseStudentsPage />} />
            <Route path="academy/admin" element={<AcademyAdminPage />} />

            {/* Setup */}
            <Route path="fixed-costs" element={<FixedCostsPage />} />
            <Route path="services" element={<ServicesPage />} />
            <Route path="service-kits" element={<ServiceKitsPage />} />
            <Route path="price-guide" element={<PriceGuidePage />} />

            {/* Daily Ops */}
            <Route path="jobs" element={<JobsPage />} />
            <Route path="job-risk-calculator" element={<JobRiskCalculatorPage />} />
            <Route path="quick-quote" element={<QuickQuotePage />} />
            <Route path="weekly-schedule" element={<WeeklySchedulePage />} />
            <Route path="bookings" element={<BookingsPage />} />
            <Route path="trip-log" element={<CrossCityPlannerPage />} />
            <Route path="local-routes" element={<LocalRoutesPage />} />
            <Route path="dispatch" element={<DispatchBoardPage />} />
            <Route path="rep-dropoff" element={<RepDropoffPage />} />
            <Route path="qr-settings" element={<QrSettingsPage />} />

            {/* Clients & Financing */}
            <Route path="clients" element={<ClientsPage />} />
            <Route path="clients/:id" element={<ClientDetailPage />} />
            <Route path="intakes" element={<ClientIntakesPage />} />
            <Route path="quotes" element={<QuotesPage />} />
            <Route path="team" element={<TeamPage />} />
            <Route path="team-availability" element={<TeamAvailabilityPage />} />
            <Route path="my-availability" element={<MyAvailabilityPage />} />
            <Route path="me" element={<MyProfilePage />} />
            <Route path="my-performance" element={<MyPerformancePage />} />
            <Route path="performance" element={<PerformanceAdminPage />} />
            <Route path="performance/settings" element={<PerfSettingsPage />} />
            <Route path="i9" element={<I9CompliancePage />} />
            <Route path="hydration" element={<HydrationStationPage />} />
            <Route path="heat-safety" element={<HeatSafetyPage />} />
            <Route path="loyalty/redemptions" element={<RedemptionQueuePage />} />
            <Route path="financing-hub" element={<FinancingHubPage />} />
            <Route path="financing-plans" element={<FinancingPlansPage />} />
            <Route path="financing-loans" element={<FinancingLoansPage />} />
            <Route path="loan-payments" element={<LoanPaymentsPage />} />
            <Route path="payment-schedule" element={<PaymentSchedulePage />} />
            <Route path="default-tracker" element={<DefaultTrackerPage />} />
            <Route path="rewards" element={<RewardsLoyaltyPage />} />
            <Route path="invoices" element={<InvoicesPage />} />
            <Route path="invoices/:invoice_id" element={<InvoiceDetailPage />} />

            {/* Accounting */}
            <Route path="business-accounting" element={<BusinessAccountingHub />} />
            <Route path="business-accounting/company-debt" element={<CompanyDebtPage />} />
            <Route path="business-accounting/pnl" element={<PLStatementPage />} />
            <Route path="business-accounting/cash-flow" element={<CashFlowStatementPage />} />
            <Route path="business-accounting/balance-sheet" element={<BalanceSheetPage />} />
            <Route path="business-accounting/owner-transfers" element={<OwnerTransfersPage />} />
            <Route path="business-accounting/categorize" element={<LedgerCategorizerPage />} />
            <Route path="business-accounting/missing-receipts" element={<MissingReceiptsPage />} />
            <Route path="business-accounting/chain-trace" element={<ChainTracePage />} />
            <Route path="business-accounting/closeout" element={<MonthlyCloseoutPage />} />
            <Route path="monthly-archive" element={<MonthlyArchivePage />} />
            <Route path="business-accounting/auto-rules" element={<AutoRulesPage />} />
            <Route path="business-accounting/accountant-access" element={<AccountantAccessPage />} />
            <Route path="business-accounting/audit-log" element={<AuditLogPage />} />
            <Route path="business-accounting/tax-package" element={<TaxPackagePage />} />
            <Route path="ai-leads" element={<AILeadsPage />} />
            <Route path="admin/ai-intel" element={<AdminAIIntelPage />} />
            <Route path="chart-of-accounts" element={<ChartOfAccountsPage />} />
            <Route path="monthly-dashboard" element={<MonthlyDashboardPage />} />
            <Route path="accounting" element={<AccountingDashboardPage />} />
            <Route path="bills" element={<BillsPage />} />
            <Route path="transactions" element={<TransactionsPage />} />
            <Route path="tax-savings" element={<TaxSavingsPage />} />
            <Route path="business-loans" element={<BusinessLoansPage />} />
            <Route path="business-loan-payments" element={<BusinessLoanPaymentsPage />} />
            <Route path="company-savings" element={<CompanySavingsPage />} />

            {/* Fleet & Chemicals */}
            <Route path="vehicles" element={<VehiclesPage />} />
            <Route path="vehicles/:vid" element={<VehicleDetailPage />} />
            <Route path="chemicals" element={<ChemicalInventoryPage />} />
            <Route path="chemical-price-history" element={<ChemicalPriceHistoryPage />} />
            <Route path="chem-intel" element={<ChemicalIntelligencePage />} />
            <Route path="chem-intel/alerts" element={<ChemAlertsPage />} />
            <Route path="chem-intel/scanner" element={<ChemPurchaseScannerPage />} />
            <Route path="chem-intel/closeout" element={<ChemJobCloseoutPage />} />
            <Route path="chem-intel/add-from-catalog" element={<ChemAddFromCatalogPage />} />
            <Route path="admin/master-catalog" element={<MasterCatalogPage />} />
            <Route path="dev/architecture" element={<ArchitecturePage />} />
            <Route path="chem-intel/forecast" element={<ChemForecastPage />} />
            <Route path="chem-intel/quotes" element={<ChemQuoteAdjustPage />} />
            <Route path="chem-intel/admin" element={<ChemScrapeRunsPage />} />
            <Route path="chem-intel/settings" element={<ChemIntelSettingsPage />} />
            <Route path="chem-intel/:chemId" element={<ChemicalDetailPage />} />
            <Route path="gas-travel" element={<GasTravelPage />} />
            <Route path="mileage-log" element={<MileageLogPage />} />
            <Route path="equipment" element={<EquipmentPage />} />
            <Route path="resin" element={<DiResinManagerPage />} />
            <Route path="field" element={<FieldTabPage />} />
            <Route path="resin-legacy" element={<ResinPage />} />

            {/* People & Growth */}
            <Route path="payroll" element={<PayrollPage />} />
            <Route path="work-assignments" element={<WorkAssignmentsPage />} />
            <Route path="cc-rewards" element={<CCRewardsPage />} />

            {/* PREMIUM — Sales Rep + Commission System */}
            <Route path="sales-dashboard" element={<SalesCompanyDashboard />} />
            <Route path="commission-lookup" element={<CommissionLookupPage />} />
            <Route path="commission-ledger" element={<CommissionLedgerPage />} />
            <Route path="rep-quoting" element={<RepQuotingPage />} />
            <Route path="partner-network" element={<PartnerOnboardingPage />} />
            <Route path="partner-review" element={<PartnerNetworkReviewPage />} />
            <Route path="rep-offers" element={<RepOffersPage />} />
            <Route path="marketplace" element={<MarketplacePage />} />
            <Route path="bonus-tracker" element={<BonusTrackerPage />} />
            <Route path="sales-reps" element={<SalesRepsPage />} />
            <Route path="qr-codes" element={<QRCodesPage />} />
            <Route path="card-placements" element={<CardPlacementsPage />} />
            <Route path="rep-bookings" element={<RepBookingsPage />} />
            <Route path="door-to-door" element={<DoorToDoorPage />} />
            <Route path="social-referrals" element={<SocialReferralsPage />} />
            <Route path="commission-settings" element={<CommissionSettingsPage />} />
            <Route path="software-referrals" element={<SoftwareReferralsPage />} />

            {/* PLATINUM — Tool Rental Pro (18 modules) */}
            <Route path="rental" element={<ToolRentalOverviewPage />} />
            <Route path="rental/tools" element={<ToolCatalogPage />} />
            <Route path="rental/packages" element={<PackageBuilderPage />} />
            <Route path="rental/customers" element={<CustomerRegistryPage />} />
            <Route path="rental/orders" element={<RentalOrdersPage />} />
            <Route path="rental/returns" element={<ReturnsDamagesPage />} />
            <Route path="rental/repairs" element={<RepairLogPage />} />
            <Route path="rental/payments" element={<PaymentsDepositsPage />} />
            <Route path="rental/rto" element={<RentToOwnPage />} />
            <Route path="rental/fixed-costs" element={<RentalFixedCostsPage />} />
            <Route path="rental/availability" element={<AvailabilityPage />} />
            <Route path="rental/trust-scores" element={<TrustScorePage />} />
            <Route path="rental/asset-lifecycle" element={<AssetLifecyclePage />} />
            <Route path="rental/late-fee-policy" element={<LateFeePolicyPage />} />
            <Route path="rental/marketing-roi" element={<MarketingRoiPage />} />
            <Route path="rental/capital-allocation" element={<CapitalAllocationPage />} />
            <Route path="rental/pnl" element={<PnlStatementPage />} />
            <Route path="rental/financial-dashboard" element={<FinancialDashboardPage />} />
            <Route path="rental/settings" element={<RentalSettingsPage />} />

            {/* PRO — Personal Finance (4th workspace) */}
            <Route path="finance" element={<PfDashboardPage />} />
            <Route path="finance/daily-log" element={<PfDailyLogPage />} />
            <Route path="finance/monthly-budget" element={<PfMonthlyBudgetPage />} />
            <Route path="finance/bills" element={<PfBillsPage />} />
            <Route path="finance/net-worth" element={<PfNetWorthPage />} />
            <Route path="finance/debts" element={<PfDebtsPage />} />
            <Route path="finance/payoff-schedule" element={<PfPayoffSchedulePage />} />
            <Route path="finance/goals" element={<PfGoalsPage />} />
            <Route path="finance/settings" element={<PfSettingsPage />} />
            <Route path="finance/plaid" element={<PfPlaidPage />} />
            <Route path="finance/income" element={<PfIncomePage />} />
            <Route path="finance/credit-cards" element={<PfCreditCardsPage />} />
            <Route path="finance/review" element={<PfReviewPage />} />
            <Route path="finance/cash-flow" element={<PfCashFlowPage />} />
            <Route path="finance/reports" element={<PfReportsPage />} />
            <Route path="finance/emergency-fund" element={<PfEmergencyFundPage />} />
            <Route path="finance/recurring" element={<PfRecurringPage />} />
            <Route path="finance/survival" element={<SurvivalModePage />} />
            <Route path="finance/household" element={<PfHouseholdPage />} />
            <Route path="maintenance-contracts" element={<MaintenanceContractsPage />} />

            {/* Company Net Worth — business valuation module */}
            <Route path="company-worth" element={<CompanyWorthPage />} />
            <Route path="market-health" element={<MarketHealthPage />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </HashSessionGate>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
    </ThemeProvider>
  );
}
